import { useCallback, useMemo } from 'react'

import { RoleType } from '@tribeplatform/gql-client/types'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
  generateGroupBy,
} from '../utils/generateAnalyticsQuery.js'

const roleCount = Object.keys(RoleType).length

export const ActivityByRolePie = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()

  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'space_type', in: ['GROUP', 'BROADCAST'] },
        { field: 'actor_type', operator: '!=', value: null },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: (timeFrame.xAxisCount + 2) * roleCount,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [
        {
          field: 'count(post) + count(reply) + count(react)',
          as: 'Activities',
        },
      ],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    const chartQuery: AnalyticsQuery = {
      ...currentQuery,
      selects: [
        ...currentQuery.selects,
        { field: 'actor_type', as: 'actor_type' },
      ],
      groupBy: {
        ...generateGroupBy(timeFrame.granularity),
        field: 'actor_type',
      },
      order: { by: 'timeBucket', dir: 'asc' },
    }

    if (timeFrame.id === 'allTime') {
      return [
        generateAnalyticsQuery(chartQuery),
        generateAnalyticsQuery(currentQuery),
      ]
    }

    return [
      generateAnalyticsQuery(chartQuery),
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, segment, timeFrame])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Pie"
          title={$t({
            id: 'Analytics.ActivityByRolePie.ChartTitle',
            defaultMessage: 'Activity by role',
          })}
          description={timeFrame.text}
          data={data[0]}
          groupBy={[
            {
              indexBy: 'Activities',
              field: 'actor_type',
              values: [RoleType.admin],
              as: 'Admin',
            },
            {
              indexBy: 'Activities',
              field: 'actor_type',
              values: [RoleType.moderator],
              as: 'Moderator',
            },
            {
              indexBy: 'Activities',
              field: 'actor_type',
              values: [RoleType.member],
              as: 'Member',
            },
          ]}
          indexBy={['Admin', 'Moderator', 'Member']}
          labels={[
            $t({
              id: 'Roles.Admin',
              defaultMessage: 'Admin',
            }),
            $t({
              id: 'Roles.Moderator',
              defaultMessage: 'Moderator',
            }),
            $t({
              id: 'Roles.Member',
              defaultMessage: 'Member',
            }),
          ]}
          isLoading={isLoading}
          className={className}
        />
      )}
    </AnalyticsFeeder>
  )
}
